import type { Ayah, TranslationLanguage, TafsirLanguage } from '@/types/domain';
import { TafsirPanel } from './TafsirPanel';
import { JuzPill } from './JuzPill';
import { BookmarkButton } from './BookmarkButton';
import { HighlightAllahText } from '@/components/ui/HighlightAllahText';
import { cn } from '@/lib/utils/cn';
import { isDuaAyah } from '@/lib/quran/duaAyahs';

type AyahLineProps = {
  ayah: Ayah;
  translationLanguage: TranslationLanguage;
  showTransliteration: boolean;
  showTafsir: boolean;
  tafsirLanguage: TafsirLanguage;
  /** Used to render the juz/ruku pill only on the first ayah in a juz. */
  showJuzMarker?: boolean;
};

/**
 * One ayah, rendered with calm hierarchy:
 *   1. Tiny meta row (ayah number, optional juz marker)
 *   2. Arabic text (text_ar) — large, RTL, font-arabic
 *   3. Transliteration (arabic_roman) — optional, italic
 *   4. English translation (translation_en) — serif body
 *   5. Urdu translation (translation_ur) — RTL, font-arabic
 *   6. Tafsir panel — optional
 *
 * Each ayah is separated by a hairline. No boxes, no shadows.
 */
export function AyahLine({
  ayah,
  translationLanguage,
  showTransliteration,
  showTafsir,
  tafsirLanguage,
  showJuzMarker,
}: AyahLineProps) {
  const showEn = translationLanguage === 'en' || translationLanguage === 'both';
  const showUr = translationLanguage === 'ur' || translationLanguage === 'both';
  const shouldHighlightArabic = isDuaAyah(ayah.surah_id, ayah.ayah_number);

  return (
    <article
      id={`ayah-${ayah.surah_id}-${ayah.ayah_number}`}
      className="py-10 first:pt-6 border-b border-ink-200/50 dark:border-ink-800/50 last:border-b-0 scroll-mt-20"
    >
      <div className="flex items-center justify-between mb-4">
        <span
          aria-label={`Surah ${ayah.surah_id}, Ayah ${ayah.ayah_number}`}
          // Deep-teal accent pill with white text. Reads cleanly on
          // both the warm-ivory light surface and the night-900 dark
          // surface — same tone the rest of the app uses for primary
          // accents, with a thin matching ring for definition.
          className="
            inline-flex items-center justify-center
            rounded-full bg-accent px-2.5 py-0.5
            text-xs font-semibold tabular-nums tracking-wide text-white
            ring-1 ring-inset ring-white/15
            shadow-[0_1px_2px_rgba(10,107,90,0.25)]
          "
        >
          {ayah.surah_id}:{ayah.ayah_number}
        </span>
        <div className="flex items-center gap-2">
          {ayah.sajda && <span className="pill">Sajda</span>}
          {showJuzMarker && <JuzPill juz={ayah.juz} />}
          <BookmarkButton
            ayahId={ayah.id}
            surahId={ayah.surah_id}
            ayahNumber={ayah.ayah_number}
          />
        </div>
      </div>

      <p className="arabic-display mb-6">
        <span className={cn(shouldHighlightArabic && 'dua-highlight')}>
          <HighlightAllahText text={ayah.text_ar} />
        </span>
      </p>

      {showTransliteration && ayah.arabic_roman && (
        <p className="text-sm italic leading-relaxed text-ink-500 dark:text-ink-400 mb-4">
          {ayah.arabic_roman}
        </p>
      )}

      {showEn && ayah.translation_en && (
        <p
          className={cn(
            'font-serif text-lg leading-relaxed text-ink-800 dark:text-ink-100',
            showUr && 'mb-4',
          )}
        >
          <HighlightAllahText text={ayah.translation_en} />
        </p>
      )}

      {showUr && ayah.translation_ur && (
        <p className="urdu-display"><HighlightAllahText text={ayah.translation_ur} /></p>
      )}

      {showTafsir && (
        <TafsirPanel ayah={ayah} language={tafsirLanguage} />
      )}
    </article>
  );
}
